import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Spinner, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaSave, FaTimes } from 'react-icons/fa';
import { adminApi, dsoApi } from '../../services/api';
import Swal from 'sweetalert2';

const CommissionForm = () => {
  const navigate = useNavigate();
  const { dsoId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dsoCommissions, setDsoCommissions] = useState({ products: [] });
  const [dsoDetails, setDsoDetails] = useState(null);
  const [commissionData, setCommissionData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch DSO details
        const dsoResponse = await dsoApi.getDsoById(dsoId);
        setDsoDetails(dsoResponse.data);
        
        // Fetch commissions
        const commissionsResponse = await adminApi.getDsoCommissions(dsoId);
        setDsoCommissions(commissionsResponse.data);
        
        // Initialize form data
        setCommissionData(commissionsResponse.data.products.map(product => ({
          product_id: product.product_id,
          commission_percentage: product.commission_percentage,
          commission_amount: product.commission_amount
        })));
      } catch (error) {
        console.error('Error fetching commissions:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to fetch DSO commissions',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    };

    if (dsoId) {
      fetchData();
    }
  }, [dsoId]);

  const handleCancel = () => {
    navigate(`/admin/dso/${dsoId}/commissions`);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await adminApi.updateDsoCommissions(dsoId, commissionData);
      
      Swal.fire({
        title: 'Success',
        text: 'Commission settings saved successfully',
        icon: 'success',
        confirmButtonText: 'OK'
      });
      
      navigate(`/admin/dso/${dsoId}/commissions`);
    } catch (error) {
      console.error('Error saving commissions:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to save commission settings',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCommissionChange = (productId, field, value) => {
    // Parse percentage and amount as numbers or null
    let parsedValue = null;
    if (value !== '' && !isNaN(parseFloat(value))) {
      parsedValue = parseFloat(value);
    }
    
    setCommissionData(prevData => 
      prevData.map(item => 
        item.product_id === productId 
          ? { ...item, [field]: parsedValue }
          : item
      )
    );
  };

  // Group products by category
  const groupByCategory = () => {
    const grouped = {};
    
    dsoCommissions.products?.forEach(product => {
      const category = product.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(product);
    });
    
    return grouped;
  };

  // Find a product's commission data by product_id
  const getCommissionData = (productId) => {
    return commissionData.find(item => item.product_id === productId) || {
      commission_percentage: null,
      commission_amount: null
    };
  };

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
              {dsoDetails ? `Edit ${dsoDetails.name}'s Commission Settings` : 'Edit DSO Commission Settings'}
              <div>
                <Button variant="secondary" className="me-2" onClick={handleCancel} disabled={saving}>
                  <FaTimes className="me-1" /> Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave className="me-1" /> Save Changes
                    </>
                  )}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <>
                  <p className="text-muted mb-4">
                    Set commission values for each product. You can specify either a percentage or a fixed amount (or both).
                  </p>
                  
                  {Object.entries(groupByCategory()).map(([category, products]) => (
                    <div key={category} className="mb-4">
                      <h5 className="border-bottom pb-2">{category}</h5>
                      <Table striped hover responsive>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Bank</th>
                            <th style={{ width: '200px' }}>Commission Percentage (%)</th>
                            <th style={{ width: '200px' }}>Commission Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.map(product => {
                            const commission = getCommissionData(product.product_id);
                            return (
                              <tr key={product.product_id}>
                                <td>{product.name}</td>
                                <td>{product.bank || '-'}</td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={commission.commission_percentage !== null ? commission.commission_percentage : ''}
                                    onChange={(e) => handleCommissionChange(product.product_id, 'commission_percentage', e.target.value)}
                                    placeholder="Enter percentage"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={commission.commission_amount !== null ? commission.commission_amount : ''}
                                    onChange={(e) => handleCommissionChange(product.product_id, 'commission_amount', e.target.value)}
                                    placeholder="Enter amount"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ))}

                  <div className="d-flex justify-content-end mt-3">
                    <Button variant="secondary" className="me-2" onClick={handleCancel} disabled={saving}>
                      <FaTimes className="me-1" /> Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-1" /> Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CommissionForm;